import { type AssessmentResponseDTO } from '@/api/assessments/api';
import { type AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';

const analysisSkillSelectionStorageKey = (assessmentInstanceId: string): string =>
  `bdp.transcriptAnalysis.skillSelection.${assessmentInstanceId}`;

/**
 * Persist the transcript-analysis skill selector choice to sessionStorage.
 * Mirrors `writeGuideSelection` from `assessmentInterviewFlow.ts`.
 */
export const writeAnalysisSkillSelection = (
  assessmentInstanceId: string,
  selectedValue: string,
): void => {
  try {
    if (selectedValue.startsWith('template:')) {
      sessionStorage.setItem(
        analysisSkillSelectionStorageKey(assessmentInstanceId),
        JSON.stringify({ template_key: selectedValue.slice('template:'.length) }),
      );
    } else if (selectedValue.startsWith('version:')) {
      sessionStorage.setItem(
        analysisSkillSelectionStorageKey(assessmentInstanceId),
        JSON.stringify({ skill_version_id: selectedValue.slice('version:'.length) }),
      );
    }
  } catch {
    // ignore quota / private mode
  }
};

/**
 * Read back a previously persisted transcript-analysis skill selection.
 * Returns the prefixed value string (`template:<key>` or `version:<uuid>`) or null.
 */
export const readAnalysisSkillSelection = (assessmentInstanceId: string): string | null => {
  try {
    const raw = sessionStorage.getItem(analysisSkillSelectionStorageKey(assessmentInstanceId));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (typeof parsed.template_key === 'string') {
      return `template:${parsed.template_key}`;
    }
    if (typeof parsed.skill_version_id === 'string') {
      return `version:${parsed.skill_version_id}`;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Derives the canonical frontend route step from assessment response data.
 *
 * Route rules (in priority order):
 *  1. current_step > 2  → clamp to AssessmentFlowStep 3 or 4.
 *  2. current_step >= 2 AND step2_status present → use step2_status sub-routing.
 *  3. Legacy: current_step >= 2 AND interview_guide_approved_at is null
 *     → treat as approved to avoid bouncing existing records backward.
 *  4. interview_guide_approved_at is set → 'step-2'.
 *  5. interview_guide_markdown exists (not empty) → '1-review' (generated, unapproved).
 *  6. Default → 1 (no guide yet).
 */
export function deriveAssessmentFlowStep(
  dto: Pick<
    AssessmentResponseDTO,
    'current_step' | 'interview_guide_approved_at' | 'interview_guide_markdown' | 'step2_status'
  >,
): AssessmentFlowStep {
  const step = dto.current_step ?? 1;

  // Steps beyond 2 are no longer in the flow — clamp to '2-review'.
  if (step > 2) {
    return '2-review';
  }

  // Step 2 sub-routing via step2_status when available.
  if (step >= 2 && dto.step2_status) {
    switch (dto.step2_status) {
      case 'generating':
        return '2-generating';
      case 'review':
        return '2-review';
      case 'approved':
        return '2-review';
      case 'intake':
      case 'failed':
      default:
        return 2;
    }
  }

  // Legacy compatibility: records with current_step >= 2 but no approval timestamp
  // existed before the approval gate was introduced. Route them to step 2.
  if (step >= 2 && dto.interview_guide_approved_at === null) {
    return 2;
  }

  // Explicitly approved → step 2.
  if (dto.interview_guide_approved_at !== null && dto.interview_guide_approved_at !== undefined) {
    return 2;
  }

  // Guide generated but not yet approved → step 1 review.
  if (dto.interview_guide_markdown && dto.interview_guide_markdown.trim().length > 0) {
    return '1-review';
  }

  // No guide yet → beginning of step 1.
  return 1;
}
